import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, apiGet, apiPost } from '../../modules/core/index.js'
import type { InsightsSpan } from './labels.js'
import type { Briefing, BriefingRefusal, BriefingRequested, GuardRefusal, LatestBriefing } from './types.js'

/** The operator app's reads and presses of the AI briefings add-on, every one through the inspected fetch. */

/** What a briefing is about: one facility, the client across facilities, or the Insights reports for a span. */
export type BriefingTarget = { kind: 'facility'; facilityId: string } | { kind: 'client' } | { kind: 'insights'; span: InsightsSpan }

/** The target's read path. */
export function briefingReadPath(target: BriefingTarget): string {
  switch (target.kind) {
    case 'facility':
      return `/facilities/${encodeURIComponent(target.facilityId)}/briefing`
    case 'client':
      return '/briefing'
    case 'insights':
      return `/insights/briefing?span=${target.span}`
  }
}

/** The target's press: the path and the body. */
function briefingPress(target: BriefingTarget, refresh: boolean): { path: string; body: unknown } {
  switch (target.kind) {
    case 'facility':
      return { path: `/facilities/${encodeURIComponent(target.facilityId)}/briefing`, body: { refresh } }
    case 'client':
      return { path: '/briefing', body: { refresh } }
    case 'insights':
      return { path: '/insights/briefing', body: { span: target.span, refresh } }
  }
}

export function targetKey(target: BriefingTarget): string {
  return target.kind === 'facility' ? `facility:${target.facilityId}` : target.kind === 'client' ? 'client' : `insights:${target.span}`
}

export const latestBriefingKey = (target: BriefingTarget) => ['briefings', 'latest', targetKey(target)] as const
export const briefingKey = (briefingId: string) => ['briefings', 'one', briefingId] as const

/** The reader's latest briefing for the target, with the guard and the settings; `enabled` false stays idle. */
export function useLatestBriefing(target: BriefingTarget, enabled: boolean) {
  return useQuery({ queryKey: latestBriefingKey(target), queryFn: () => apiGet<LatestBriefing>(briefingReadPath(target)), enabled })
}

/** One briefing, polled at the API's `pollMs` while it or one of its questions is pending. */
export function useBriefingRecord(briefingId: string | null, pollMs: number) {
  return useQuery({
    queryKey: briefingKey(briefingId ?? ''),
    queryFn: () => apiGet<Briefing>(`/briefings/${encodeURIComponent(briefingId as string)}`),
    enabled: briefingId !== null,
    refetchInterval: (query) => {
      const briefing = query.state.data
      return briefing && (briefing.status === 'pending' || briefing.questions.some((question) => question.status === 'pending')) ? pollMs : false
    },
  })
}

/** The guard's refusal behind an `ApiError`, where the answer carried one. */
export function refusalOf(error: unknown): { refusal: GuardRefusal; retryAt: string | null; message: string } | null {
  if (!(error instanceof ApiError)) {
    return null
  }
  const details = error.details as Partial<BriefingRefusal> | undefined
  return details && typeof details.refusal === 'string' ? { refusal: details.refusal, retryAt: details.retryAt ?? null, message: error.message } : null
}

/** A press, a question and an email for one target, each refreshing the latest read and the briefing's own. */
export function useBriefingActions(target: BriefingTarget) {
  const queryClient = useQueryClient()
  const refresh = (briefingId?: string) => {
    void queryClient.invalidateQueries({ queryKey: latestBriefingKey(target) })
    if (briefingId) {
      void queryClient.invalidateQueries({ queryKey: briefingKey(briefingId) })
    }
  }
  return {
    request: useMutation({
      mutationFn: (refresh: boolean) => {
        const press = briefingPress(target, refresh)
        return apiPost<BriefingRequested>(press.path, press.body)
      },
      onSuccess: (data) => refresh(data.briefing.id),
      onError: () => refresh(),
    }),
    ask: useMutation({ mutationFn: ({ briefingId, question }: { briefingId: string; question: string }) => apiPost<{ briefing: Briefing; questionId: string }>(`/briefings/${encodeURIComponent(briefingId)}/questions`, { question }), onSuccess: (data) => refresh(data.briefing.id), onError: () => refresh() }),
    email: useMutation({ mutationFn: (briefingId: string) => apiPost<{ to: string; briefing: Briefing }>(`/briefings/${encodeURIComponent(briefingId)}/email`, {}), onSuccess: (data) => refresh(data.briefing.id), onError: () => refresh() }),
  }
}
